import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';
import { Lock, ArrowRight, CheckCircle2, AlertTriangle, KeyRound } from 'lucide-react';

const TEAL = '#4DB6AC';

/**
 * Universal reset-password page. The reset link the user clicked from the email
 * contains:
 *    /reset-password?token=...&scope=org|community
 *
 * - scope decides which backend endpoint to POST to
 * - if scope is missing/invalid we still show a friendly error
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const scope = (params.get('scope') || 'org').toLowerCase();
  const isCommunity = scope === 'community';
  const endpoint = isCommunity ? '/community/auth/reset-password' : '/auth/reset-password';
  const loginRoute = isCommunity ? '/community/login' : '/login';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const minLen = isCommunity ? 8 : 6;
    if (password.length < minLen) { setError(`Password must be at least ${minLen} characters.`); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}${endpoint}`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──
  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <CheckCircle2 size={32} color={TEAL} />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>Password updated successfully</h1>
          <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6, marginBottom: 20 }}>Please log in with your new password.</p>
          <button type="button" data-testid="go-to-login-btn" onClick={() => navigate(loginRoute)}
            style={{ width: '100%', padding: '12px', background: TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Bad link screen ──
  if (tokenMissing) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <AlertTriangle size={32} color="#B91C1C" />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>Invalid reset link</h1>
          <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6, marginBottom: 20 }}>This reset link is missing or malformed. Please request a new one.</p>
          <button type="button" onClick={() => navigate(loginRoute)}
            style={{ width: '100%', padding: '12px', background: TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${TEAL}, #2E8B7A)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <KeyRound size={26} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>Choose a new password</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>Make it at least {isCommunity ? 8 : 6} characters.</p>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {error && (
            <div data-testid="reset-password-error" style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 16 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input data-testid="reset-password-new" type="password" required placeholder="New password"
                value={password} onChange={e => setPassword(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }} />
            </div>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input data-testid="reset-password-confirm" type="password" required placeholder="Confirm new password"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }} />
            </div>
            <button type="submit" data-testid="reset-password-submit" disabled={loading}
              style={{ width: '100%', padding: '14px', background: loading ? '#9CA3AF' : TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
              {loading ? 'Updating…' : (<>Update password <ArrowRight size={18} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
